import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  X, ShoppingBag, Trash2, Plus, Minus, ArrowRight,
  ShoppingCart, Package, Sparkles,
} from 'lucide-react'
import { useCart } from '../contexts/CartContext'
import { useSettings } from '../contexts/SettingsContext'
import { formatPrice } from '../utils/format'
import './CartDrawer.css'

const DELIVERY_FEE = 0 // livraison gratuite

/* ── Ligne article ── */
function CartItem({ item, lang }) {
  const { updateQuantity, removeFromCart } = useCart()
  const name = lang === 'fr' ? item.product?.name?.fr : item.product?.name?.en
  const img  = item.product?.primary_image

  return (
    <div className="cd-item">
      <div className="cd-item-img-wrap">
        {img ? (
          <img src={img} alt={name} className="cd-item-img" />
        ) : (
          <div className="cd-item-img-placeholder"><Package size={20} /></div>
        )}
      </div>

      <div className="cd-item-body">
        <div className="cd-item-header">
          <p className="cd-item-name">{name ?? (lang === 'fr' ? 'Produit' : 'Product')}</p>
          <button
            className="cd-item-remove"
            onClick={() => removeFromCart(item.id)}
            aria-label="Supprimer"
          >
            <Trash2 size={14} />
          </button>
        </div>

        <div className="cd-item-footer">
          <div className="cd-qty">
            <button
              className="cd-qty-btn"
              onClick={() => updateQuantity(item.id, item.quantity - 1)}
              disabled={item.quantity <= 1}
            >
              <Minus size={11} />
            </button>
            <span className="cd-qty-val">{item.quantity}</span>
            <button
              className="cd-qty-btn"
              onClick={() => updateQuantity(item.id, item.quantity + 1)}
              disabled={item.quantity >= (item.product?.stock ?? 99)}
            >
              <Plus size={11} />
            </button>
          </div>
          <span className="cd-item-price">{formatPrice(item.unit_price * item.quantity)}</span>
        </div>
      </div>
    </div>
  )
}

/* ── Empty state ── */
function EmptyCart({ lang }) {
  return (
    <div className="cd-empty">
      <div className="cd-empty-icon">
        <ShoppingCart size={48} strokeWidth={1.2} />
      </div>
      <p className="cd-empty-title">{lang === 'fr' ? 'Votre panier est vide' : 'Your cart is empty'}</p>
      <p className="cd-empty-sub">
        {lang === 'fr'
          ? 'Découvrez nos produits et ajoutez vos favoris'
          : 'Browse our products and add your favourites'}
      </p>
      <div className="cd-empty-spark"><Sparkles size={14} /></div>
    </div>
  )
}

/* ══════════════════════════════════════════════
   CartDrawer
══════════════════════════════════════════════ */
export default function CartDrawer() {
  const { lang } = useSettings()
  const {
    items, itemCount, subtotal,
    cartOpen, closeCart, clearCart,
  } = useCart()

  const drawerRef = useRef(null)
  const total = subtotal + DELIVERY_FEE

  /* Fermer avec Escape */
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') closeCart() }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
  }, [closeCart])

  /* Bloquer le scroll body quand ouvert */
  useEffect(() => {
    document.body.style.overflow = cartOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [cartOpen])

  return (
    <>
      {/* Backdrop */}
      <div
        className={`cd-backdrop ${cartOpen ? 'open' : ''}`}
        onClick={closeCart}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        ref={drawerRef}
        className={`cd-drawer ${cartOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={lang === 'fr' ? 'Panier' : 'Cart'}
      >
        {/* ── Header ── */}
        <div className="cd-header">
          <div className="cd-header-left">
            <ShoppingBag size={19} className="cd-header-icon" />
            <span className="cd-header-title">
              {lang === 'fr' ? 'Mon Panier' : 'My Cart'}
            </span>
            {itemCount > 0 && (
              <span className="cd-header-count">{itemCount}</span>
            )}
          </div>
          <div className="cd-header-actions">
            {items.length > 0 && (
              <button
                className="cd-clear-btn"
                onClick={clearCart}
                title={lang === 'fr' ? 'Vider le panier' : 'Clear cart'}
              >
                <Trash2 size={14} />
                {lang === 'fr' ? 'Vider' : 'Clear'}
              </button>
            )}
            <button className="cd-close" onClick={closeCart} aria-label="Fermer">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="cd-body">
          {items.length === 0 ? (
            <EmptyCart lang={lang} />
          ) : (
            <div className="cd-items">
              {items.map(item => (
                <CartItem key={item.id} item={item} lang={lang} />
              ))}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        {items.length > 0 && (
          <div className="cd-footer">
            {/* Récap */}
            <div className="cd-summary">
              <div className="cd-summary-row">
                <span>{lang === 'fr' ? 'Sous-total' : 'Subtotal'}</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="cd-summary-row">
                <span>{lang === 'fr' ? 'Livraison' : 'Delivery'}</span>
                <span className="cd-free">
                  {DELIVERY_FEE === 0
                    ? (lang === 'fr' ? 'Gratuite' : 'Free')
                    : formatPrice(DELIVERY_FEE)}
                </span>
              </div>
              <div className="cd-summary-total">
                <span>Total</span>
                <span>{formatPrice(total)}</span>
              </div>
            </div>

            {/* CTA Checkout */}
            <Link to="/checkout" className="cd-checkout-btn" onClick={closeCart}>
              <ShoppingBag size={17} />
              {lang === 'fr' ? 'Passer la commande' : 'Place Order'}
              <ArrowRight size={16} className="cd-arrow" />
            </Link>

            <Link to="/catalogue" className="cd-continue" onClick={closeCart}>
              {lang === 'fr' ? 'Continuer mes achats' : 'Continue shopping'}
            </Link>
          </div>
        )}
      </aside>
    </>
  )
}
